import { Observable, Observer } from "@reactivex/rxjs";
import { EntityContainer } from "./entity-container";
import { EntityIdentifier } from "./entity-identifier";
import { ErrorDetails } from "../loggingSvc";

/** Base for all entity related events */
export interface EntityEvent {
  /** The name for this event */
  eventName: string;
}

/** When an entity has a value associated with it */
export interface EntityResult<EntityType> extends EntityEvent {
  /** The value of an entity event */
  entityValue: EntityType;
}

/** When an entity has been created */
export interface CreatedEntityResult<Identifier extends EntityIdentifier, EntityType> extends EntityResult<EntityType> {
  /** The identifier for this entity event */
  entityIdentifier: Identifier;
}

/** When an action associated with an entity has completed */
export interface ActionResult<T> extends EntityEvent {
  /** The data from the action */
  eventData: T;
}

/** Updates associated with an entity service  */
export interface EntityServiceEvent<Identifier extends EntityIdentifier, EntityType> {
  /** When this event happened*/
  eventTime: Date;
  /** The type as a string for this entity */
  entityType: string;
  /** The name for this event */
  eventName?: string;
  /** The data from an action (see entityValue for the value of a retrieved/updated event) */
  eventData?: any;
  /** The error information for this event */
  eventError?: ErrorDetails;
  /** The identifier for this entity event */
  entityIdentifier?: Identifier;
  /** The value of an entity event */
  entityValue?: EntityType;
}

export abstract class EntityService<Identifier extends EntityIdentifier, EntityType> {
  private trackedEntities = new Map<string, EntityContainer<EntityType>>();

  /**
   * @param entityType a unique type name for the entity
   */
  constructor(private entityType: string) {}

  /** Creates a new instance of a remote entity */
  protected createValue(creater: Observable<CreatedEntityResult<Identifier, EntityType>>): Observable<EntityType> {
    let container = new EntityContainer<EntityType>();
    container.update(creater);
    creater.take(1).subscribe(
      (result: CreatedEntityResult<Identifier, EntityType>) => {
        if (this.hasContainer(result.entityIdentifier)) {
          container.update(Observable.throw("Cannot create an that already exists, use updateValue instead"));
        } else {
          this.setContainer(result.entityIdentifier, container);
        }
      },
      () => {
        // Take no action on an error, the container update will have propigated any errors
      }
    );

    return container.getSubject();
  }

  /** Refreshes an entity from a remote source */
  protected refreshValue(key: Identifier): Observable<EntityType> {
    let container = this.getOrAddContainer(key);
    let refresher = this.refresh(key);
    container.update(refresher);
    return container.getSubject();
  }

  /** gets the value of an entity from the remote source or from a local cache */
  protected getValue(key: Identifier, eventName: string): Observable<EntityType> {
    if (!this.hasContainer(key) || !this.getContainer(key).hasValue()) {
      return this.refreshValue(key);
    }

    let container = this.getContainer(key);
    return container.getSubject();
  }

  /** updates the value of an entity on a remote source */
  protected updateValue(key: Identifier, updater: Observable<EntityResult<EntityType>>): Observable<EntityType> {
    let container = this.getOrAddContainer(key);
    container.update(updater);
    return container.getSubject();
  }

  /** performs an action and then refreshes the entity */
  protected performActionAndRefreshValue<T>(key: Identifier, action: Observable<ActionResult<T>>): Observable<T> {
    return Observable.create((observer: Observer<T>) => {
      this.getOrAddContainer(key).waitWhile(action);
      action.take(1).subscribe(
        (result: ActionResult<T>) => {
          this.refreshValue(key)
            .take(1)
            .subscribe(
              () => {
                observer.next(result.eventData);
                observer.complete();
              },
              (error: any) => observer.error(error)
            );
        },
        (error: any) => {
          observer.error(error);
        }
      );
    });
  }

  /** performs an action that deletes an entity from a remote source */
  protected performActionAndDeleteValue<T>(key: Identifier, action: Observable<ActionResult<T>>): Observable<T> {
    return Observable.create((observer: Observer<T>) => {
      this.getOrAddContainer(key).waitWhile(action);
      action.take(1).subscribe(
        (result: ActionResult<T>) => {
          if (this.hasContainer(key)) {
            this.getContainer(key).complete();
            this.deleteContainer(key);
          }
          observer.next(result.eventData);
          observer.complete();
        },
        (error: any) => {
          observer.error(error);
        }
      );
    });
  }

  private getOrAddContainer(key: Identifier): EntityContainer<EntityType> {
    if (!this.hasContainer(key)) {
      this.setContainer(key, new EntityContainer<EntityType>());
    }
    return this.getContainer(key);
  }

  private getContainer(key: Identifier) {
    return this.trackedEntities.get(JSON.stringify(key.toJson()));
  }

  private hasContainer(key: Identifier) {
    return this.trackedEntities.has(JSON.stringify(key.toJson()));
  }

  private setContainer(key: Identifier, value: EntityContainer<EntityType>) {
    this.trackedEntities.set(JSON.stringify(key.toJson()), value);
  }

  private deleteContainer(key: Identifier) {
    this.trackedEntities.delete(JSON.stringify(key.toJson()));
  }

  /**
   * Retrieves a fresh copy of the entity from the remote source
   * Note: the copy of the entity *must not* from a cached
   *
   * @param key The identifier for the entity that will be retrieved
   * @returns An observable that will generate an EntityResult for the retrieved entity
   */
  protected abstract refresh(key: Identifier): Observable<EntityResult<EntityType>>;
}
